/**
 * Results dialog for Docker auto-discovery (Workstream B3), Variant A.
 *
 * Lists reachable database containers as rows, each with a credentials badge
 * and a per-row Add button that saves the connection (nothing auto-connects).
 * Duplicates of existing saved connections are badged with a disabled Add.
 * Handles the detecting (spinner), found, empty, and no-Docker states.
 *
 * Opened from the New Connection dialog it sits ON TOP of it (Option C, C3):
 * closing it after one or more adds tells the dialog under it
 * (`connectionDialogHostStore`) — with no draft input the New Connection dialog
 * closes itself; with a draft it stays and says what was saved, and "View saved
 * connections" is not offered (opening Saved Connections would unmount the draft).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Container, ShipWheel, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useConnectionStore } from '../store/connectionStore';
import { useConnectionsUIStore } from '../store/uiStore';
import { useOriginatingHost } from '../store/connectionDialogHostStore';
import { useDockerDiscoveryStore } from './dockerDiscoveryStore';
import { DockerDiscoveryRow } from './DockerDiscoveryRow';
import { addDockerCandidate } from './dockerDiscoveryService';
import { findDuplicateDockerConnection } from './dockerDiscoveryUtils';
import type { DockerCandidate } from '../../../types/dockerDiscovery';

// Windows is the only platform the native SQL Server probe runs on (C2); the
// no-Docker empty state must not promise instance detection elsewhere.
// Computed per-call (not cached at module scope) so it reflects the actual
// runtime platform even across hot reloads / tests.
function isWindowsPlatform(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.platform?.toLowerCase().includes('win'));
}

function dialogTitle(status: string, outcome: string | undefined, count: number): string {
  if (status === 'detecting') return 'Detecting local databases…';
  if (outcome === 'no_docker') return 'No local databases found';
  if (outcome === 'none' || count === 0) return 'No local databases found';
  return 'Local databases found';
}

// Kind-aware count subtitle — a mixed result must not claim every candidate
// is a "running database container" when some are native local instances.
function subtitleText(candidates: DockerCandidate[]): string | null {
  if (candidates.length === 0) return null;
  const dockerCount = candidates.filter((c) => c.kind === 'docker').length;
  const localCount = candidates.filter((c) => c.kind === 'local').length;
  if (localCount === 0) {
    return `${dockerCount} running database container${dockerCount === 1 ? '' : 's'}`;
  }
  if (dockerCount === 0) {
    return `${localCount} local SQL Server instance${localCount === 1 ? '' : 's'}`;
  }
  return `${candidates.length} databases found (${dockerCount} container${
    dockerCount === 1 ? '' : 's'
  }, ${localCount} local instance${localCount === 1 ? '' : 's'})`;
}

export function DockerDiscoveryDialog() {
  const isOpen = useDockerDiscoveryStore((s) => s.isOpen);
  const status = useDockerDiscoveryStore((s) => s.status);
  const result = useDockerDiscoveryStore((s) => s.result);
  const close = useDockerDiscoveryStore((s) => s.close);
  const connections = useConnectionStore((s) => s.connections);
  const setShowNewConnectionDialog = useConnectionsUIStore((s) => s.setShowNewConnectionDialog);
  const setShowConnectionsDialog = useConnectionsUIStore((s) => s.setShowConnectionsDialog);
  // The New Connection dialog these results opened on top of, while it is still there (C3-05).
  const originHost = useOriginatingHost(isOpen);
  const host = originHost();
  const { track } = useAnalytics();

  const [addedKeys, setAddedKeys] = useState<Record<string, boolean>>({});
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const isWindows = isWindowsPlatform();

  // Detection is stateless: each fresh run clears the per-open added state so
  // duplicate detection against saved connections becomes the single source of
  // truth for "already added" (the dialog stays mounted between opens).
  // Each run counts: an add settles into the run it started in (C3 review r2, C3-12 — as an import save does), so an add
  // held across Close → Detect again is never reported by the next open to the dialog that one sits on.
  const detectRun = useRef(0);
  useEffect(() => {
    if (status === 'detecting') {
      detectRun.current += 1;
      setAddedKeys({});
      setAddingKey(null);
    }
  }, [status]);

  const candidates = useMemo(() => result?.candidates ?? [], [result]);
  const outcome = result?.outcome;

  const handleAdd = async (key: string) => {
    const candidate = candidates.find((c) => c.key === key);
    if (!candidate) return;
    setAddingKey(key);
    const run = detectRun.current;
    try {
      const ok = await addDockerCandidate(candidate);
      if (ok && detectRun.current === run) setAddedKeys((prev) => ({ ...prev, [key]: true }));
    } finally {
      if (detectRun.current === run) setAddingKey(null);
    }
  };

  // Number of connections added during this open. Detection may keep the dialog
  // open across several adds, so we surface a single-click path to review them
  // only once at least one has been saved.
  const addedCount = useMemo(
    () => Object.values(addedKeys).filter(Boolean).length,
    [addedKeys]
  );

  // A New Connection dialog under this one that holds a draft must stay (C3).
  const hostKeepsDraft = addedCount > 0 && host?.hasInput() === true;
  const completion = { via: 'docker' as const, count: addedCount, sourceLabel: 'Detect local databases' };

  // Every close: after adds, tell the New Connection dialog under it (it closes itself when it has no draft input).
  const finish = () => {
    close();
    if (addedCount > 0) originHost()?.completed(completion);
  };

  // Close the discovery dialog (and a New Connection dialog behind it with no
  // draft) and open Saved Connections so the user can see what they just added.
  const handleViewSavedConnections = () => {
    track('feature:docker_view_saved', { count: addedCount });
    close();
    const dialogHost = originHost();
    if (dialogHost) dialogHost.completed(completion);
    else setShowNewConnectionDialog(false);
    setShowConnectionsDialog(true);
  };

  const subtitle = subtitleText(candidates);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && finish()}>
      <DialogContent
        className="sm:max-w-[560px] max-h-[85vh] flex flex-col"
        data-testid="docker-discovery-dialog"
        // On top of the New Connection dialog (C3): focus goes back to the entry that opened this flow.
        onCloseAutoFocus={(event) => {
          const dialogHost = originHost();
          if (!dialogHost) return;
          event.preventDefault();
          dialogHost.restoreFocus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{dialogTitle(status, outcome, candidates.length)}</DialogTitle>
        </DialogHeader>

        {subtitle && <p className="-mt-1 text-xs text-muted-foreground">{subtitle}</p>}

        {status === 'detecting' ? (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center"
            data-testid="docker-discovery-loading"
          >
            <RandomDotmSpinner size={20} />
            <p className="text-sm text-muted-foreground">Querying Docker…</p>
          </div>
        ) : outcome === 'no_docker' ? (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-2 py-8 text-center"
            data-testid="docker-discovery-no-docker"
          >
            <ShipWheel className="h-8 w-8 opacity-40 text-muted-foreground" aria-hidden="true" />
            {isWindows ? (
              <>
                <p className="text-sm text-muted-foreground">
                  No Docker containers or local SQL Server instances found.
                </p>
                <p className="max-w-[360px] text-xs text-muted-foreground">
                  Docker is optional — installed SQL Server instances (SQLEXPRESS, LocalDB) are
                  detected on their own.
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Docker socket not found — is Docker running?
              </p>
            )}
          </div>
        ) : candidates.length === 0 ? (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-2 py-8 text-center"
            data-testid="docker-discovery-empty"
          >
            <Container className="h-8 w-8 opacity-40 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">No running database containers found.</p>
            <p className="max-w-[360px] text-xs text-muted-foreground">
              Only running containers with recognized database images are listed. Start your
              database in Docker, then detect again.
            </p>
          </div>
        ) : (
          <ScrollArea className="flex-1 min-h-0 -mx-1 pr-2">
            {candidates.map((candidate) => (
              <DockerDiscoveryRow
                key={candidate.key}
                candidate={candidate}
                duplicateConnection={findDuplicateDockerConnection(candidate, connections)}
                added={Boolean(addedKeys[candidate.key])}
                adding={addingKey === candidate.key}
                onAdd={() => void handleAdd(candidate.key)}
              />
            ))}
          </ScrollArea>
        )}

        <div className="flex items-center justify-between gap-3 border-t pt-3">
          {/* A polite live region: "Added 1 connection…" is announced when an Add saves. */}
          <p role="status" aria-live="polite" className="min-w-0 flex-1 text-xs text-muted-foreground" data-testid="docker-discovery-footer-status">
            {addedCount === 0
              ? 'Running Docker database containers and installed SQL Server instances are listed.'
              : hostKeepsDraft
                ? `Added ${addedCount} connection${addedCount === 1 ? '' : 's'}. Close to return to your new connection.`
                : `Added ${addedCount} connection${addedCount === 1 ? '' : 's'} — add more, or review them in Saved Connections.`}
          </p>
          <div className="flex flex-shrink-0 items-center gap-2">
            <Button variant="ghost" onClick={finish} data-testid="docker-discovery-close-button">
              Close
            </Button>
            {addedCount > 0 && !hostKeepsDraft && (
              <Button
                className="gap-2"
                onClick={handleViewSavedConnections}
                data-testid="docker-discovery-view-saved-button"
              >
                View saved connections
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
